import React, { FunctionComponent, useEffect, useRef } from 'react'
import { View } from 'react-native'
import { connect, ConnectedProps, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { noop } from 'lodash-es'
import { bindActionCreators } from 'redux'
import styled from 'styled-components'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  getDbtTeensSchedulingParticipantNames,
  getDbtTeensSchedulingSessionTypeOptions,
  getNextDbtTeensSessionNumber,
} from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils/dbtTeensScheduling'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'
import NavButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/navButton/NavButton'
import CardFullWidth from '@lyrahealth-inc/ui-core/lib/molecules/layouts/cardFullWidth/CardFullWidth'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { EligibilityErrorBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/eligibilityErrorBanner/EligibilityErrorBanner'
import {
  BookingSteps,
  SessionDetails,
} from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/sessionDetails/SessionDetails'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import {
  ScheduleSessionFullScreenHeaderConfig,
  useScheduleSessionFullScreenHeader,
} from './ScheduleSessionFullScreenLayout'
import { APPOINTMENT_OPERATIONS, getMeetingFormats, programConfig } from '../../common/constants/appConstants'
import {
  CLIENTS_NEW_SESSION_DETAILS,
  CLIENTS_RESCHEDULE_SESSION_DETAILS,
  CLIENTS_SESSIONS,
} from '../../common/constants/routingConstants'
import { isEngageDAProvider } from '../../common/utils/utils'
import {
  getAuthInPersonPreference,
  getAuthRoles,
  getAuthUser,
  getAuthVideoPreference,
} from '../../data/auth/authSelectors'
import { getClientAppointmentDetails } from '../../data/lyraTherapy/clientSelectors'
import { RootState, useAppDispatch } from '../../data/store'
import { useEapEligibilityError } from '../../hooks/useEapEligibilityError'
import { useAssignmentsData } from '../assignments/data/hooks/useAssignmentsData'
import { clearSelectedAppointment, setAppointment } from '../clients/clientDetails/data/appointmentsSlice'
import { useClientAppointmentsData } from '../clients/clientDetails/data/hooks/useClientAppointmentsData'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { useProgramCoverageBreakdown } from '../clients/clientDetails/hooks/useProgramCoverageBreakdown'
import { useHPISelfPayStatus } from '../clients/clientDetails/redesigned/hooks/useHPISelfPayStatus'
import { useSelfPayContinuationOfCareBanner } from '../clients/clientDetails/redesigned/hooks/useSelfPayContinuationOfCareBanner'
import { useSelectedEpisodeForClient } from '../episodes/data/hooks/useSelectedEpisodeForClient'

/**
 * Allows a provider to specify details of the appointment they are scheduling between themselves
 * and a client. Details include:
 * * The modality of the appointment (video, phone)
 * * The duration of the appointment (25, 50, 80 minutes)
 *
 * Additionally, if the provider is scheduling a new appointment, they will be able to specify:
 * * The frequency for which the given appointment should repeat (none, biweekly)
 * * The number of sessions that should be created, if there is a repeating frequency specified
 * These details will not appear if the provider is rescheduling an existing appointment.
 */

const CardFullWidthDetailsCard = styled(CardFullWidth)({
  padding: '40px',
  marginBottom: '80px',
})

const FullScreenContentContainer = styled.div({
  width: '85%',
  margin: '0 auto',
  paddingBottom: '80px',
})

const EligibiltyErrorBannerContainer = styled.div({
  width: '85%',
  margin: '40px auto 0',
  padding: '0 16px',
})

const SessionDetailsContainer: FunctionComponent<SessionDetailsContainerProps> = ({ operation, appointment }) => {
  const dispatch = useAppDispatch()
  const { isDBTTeensEnabled } = useFlags()
  const user = useSelector(getAuthUser)
  const navigate = useNavigate()
  const roles: string[] = useSelector(getAuthRoles) ?? []
  const inPersonPreference = useSelector(getAuthInPersonPreference) ?? false
  const videoPreference = useSelector(getAuthVideoPreference) ?? true
  const { data: clientDetails } = useSelectedClientData()

  // When rendered inside ScheduleSessionFullScreenLayout, the shared header owns
  // the title/exit/primary button; this step just registers its "Next" action.
  const fullScreenHeader = useScheduleSessionFullScreenHeader()
  const submitRef = useRef<() => void>()

  // Get episode data from RTK Query when redesigned client flags are enabled
  const { data: currentSelectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }
  const isDbtTeensCreateSession =
    isDBTTeensEnabled &&
    operation === APPOINTMENT_OPERATIONS.NEW &&
    currentSelectedEpisode?.program_name === ProgramNames.DialecticalBehaviorTherapyTeens
  const { data: assignments } = useAssignmentsData(undefined, { skip: !isDbtTeensCreateSession })
  const {
    data: appointments,
    isSuccess: hasLoadedAppointments,
    isLoading: isLoadingAppointments,
  } = useClientAppointmentsData(undefined, {
    skip: !isDbtTeensCreateSession,
  })
  // The session type options are derived from the appointment history, so rendering the form before
  // it lands would offer a session-1 client every option except the intake one it needs. A failed
  // fetch leaves isLoading false, so the form still renders (without intake) rather than hanging.
  const isAwaitingDbtTeensSessionTypes = isDbtTeensCreateSession && isLoadingAppointments

  const { teenName, caregiverName } = getDbtTeensSchedulingParticipantNames({
    client: clientDetails,
    episode: currentSelectedEpisode,
    appointments: appointments ?? [],
    assignments,
  })
  const dbtTeensSessionTypeOptions = isDbtTeensCreateSession
    ? getDbtTeensSchedulingSessionTypeOptions({
        teenName,
        caregiverName,
        sessionNumber: hasLoadedAppointments
          ? getNextDbtTeensSessionNumber({
              appointments: appointments ?? [],
              episodeId: currentSelectedEpisode.id,
            })
          : undefined,
      })
    : []

  const liveMessagingEnabled = clientDetails?.live_messaging ?? false
  const eligibilityError = useEapEligibilityError({
    programName: currentSelectedEpisode?.program_name,
  })

  const episodeProgramConfig = currentSelectedEpisode?.program_name
    ? programConfig[currentSelectedEpisode.program_name as keyof typeof programConfig]
    : undefined

  const programCoverageBreakdown = useProgramCoverageBreakdown(clientDetails?.employer, clientDetails?.country)
  const { banner: hpiSelfPayBanner } = useHPISelfPayStatus(
    clientDetails,
    currentSelectedEpisode?.program_name,
    programCoverageBreakdown,
  )
  const selfPayContinuationBanner = useSelfPayContinuationOfCareBanner(
    clientDetails,
    currentSelectedEpisode?.program_name,
  )

  const onBack = () => {
    dispatch(clearSelectedAppointment())
    operation === APPOINTMENT_OPERATIONS.NEW ? navigate(CLIENTS_SESSIONS.route) : navigate(-1)
  }
  let displayRepeatingFields = false
  let onSessionSubmission = noop
  let appointmentType = 'new'
  let appointmentStatus
  // The following switch/case block allows for this component to manage both create new session and rescheduling session.
  switch (operation) {
    case APPOINTMENT_OPERATIONS.NEW:
      displayRepeatingFields = true
      onSessionSubmission = (appointment: Appointment) => {
        dispatch(setAppointment(appointment))
        navigate(CLIENTS_NEW_SESSION_DETAILS.route)
      }
      appointmentStatus = 'newAppt'
      appointmentType = 'new'
      break
    case APPOINTMENT_OPERATIONS.RESCHEDULE:
      displayRepeatingFields = false
      onSessionSubmission = (appointment: Appointment) => {
        dispatch(setAppointment(appointment))
        navigate(CLIENTS_RESCHEDULE_SESSION_DETAILS.route)
      }
      appointmentType = 'rescheduled'
      appointmentStatus = 'rescheduled'
      break
  }

  const appointmentDuration =
    appointmentType === 'rescheduled'
      ? appointment?.appointmentDuration
      : episodeProgramConfig?.defaultAppointmentDuration
  const initialValues = {
    appointmentDuration: appointmentDuration != null ? appointmentDuration : undefined,
    meetingFormat: appointmentType === 'rescheduled' ? appointment?.meetingFormat : 'video',
    sessionFrequency: '0',
    appointmentStatus,
    ...(isDbtTeensCreateSession && { sessionType: appointment?.sessionType }),
  }

  // liveMessagingEnabled is a customer property and text_sessions_enabled is a property belonging to the provider
  const isLiveMessagingEnabled = (liveMessagingEnabled && user?.text_sessions_enabled) || false
  const exclusions = {
    exclusions: [
      ...(!isEngageDAProvider(roles) || videoPreference ? [] : [MEETING_FORMATS.VIDEO]),
      ...(isLiveMessagingEnabled ? [] : [MEETING_FORMATS.LIVE_MESSAGING]),
      ...(inPersonPreference ? [] : [MEETING_FORMATS.IN_PERSON]),
      ...(isEngageDAProvider(roles) ? [MEETING_FORMATS.PHONE] : []),
    ],
  }
  // if live messaging is not enabled, filter out live_messaging as an option
  const enabledMeetingFormats = getMeetingFormats(exclusions)

  // In the full-screen flow the shared header's "Next" button submits this form via the ref.
  useEffect(() => {
    if (!fullScreenHeader) {
      return
    }
    const config: ScheduleSessionFullScreenHeaderConfig = {
      primaryLabel: 'Next',
      onPrimary: () => submitRef.current?.(),
    }
    fullScreenHeader.setHeader(config)
  }, [fullScreenHeader])

  const sessionDetailsForm = isAwaitingDbtTeensSessionTypes ? (
    <LoadingIndicator size={45} />
  ) : (
    <SessionDetails
      appointment={appointment as Appointment}
      appointmentType={appointmentType}
      displayRepeatingFields={displayRepeatingFields}
      meetingFormats={enabledMeetingFormats}
      displayAppointmentDuration
      headerBanner={hpiSelfPayBanner ?? selfPayContinuationBanner}
      initialValues={initialValues as any}
      onSubmitForm={onSessionSubmission}
      onCancel={() => navigate(CLIENTS_SESSIONS.route)}
      partyName={clientDetails?.full_name || ''}
      appointmentDurationOptions={episodeProgramConfig?.appointmentDurationOptions}
      nextStep={BookingSteps.SCHEDULE}
      sessionFrequencyOptions={episodeProgramConfig?.sessionFrequencyOptions}
      handleSubmitRef={fullScreenHeader ? submitRef : undefined}
      hideSubmitButton={!!fullScreenHeader}
      hideTitle={!!fullScreenHeader}
      meetingFormatLabel={isDbtTeensCreateSession ? 'Modality' : undefined}
      sessionTypeOptions={dbtTeensSessionTypeOptions}
      title={isDbtTeensCreateSession ? 'Create new session' : undefined}
    />
  )

  if (fullScreenHeader) {
    return (
      <View testID={tID('SesssionDetailsContainer')}>
        {eligibilityError && (
          <EligibiltyErrorBannerContainer>
            <EligibilityErrorBanner />
          </EligibiltyErrorBannerContainer>
        )}
        <FullScreenContentContainer>{sessionDetailsForm}</FullScreenContentContainer>
      </View>
    )
  }

  return (
    <View testID={tID('SesssionDetailsContainer')}>
      <div>
        {eligibilityError && <EligibilityErrorBanner />}
        <NavButton text='Sessions' styleType='back' onClick={onBack} />
        <CardFullWidthDetailsCard withShadow={false} roundCorners>
          {sessionDetailsForm}
        </CardFullWidthDetailsCard>
      </div>
    </View>
  )
}

type SessionDetailsContainerProps = ConnectedProps<typeof connector> & {
  operation: string
}

const mapStateToProps = (state: RootState) => {
  return {
    appointment: getClientAppointmentDetails(state),
  }
}

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators({}, dispatch),
})

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(SessionDetailsContainer)
